/* Singles Browser — client-rendered browse experience for TCG singles
   collections, backed by the Storefront API products(query:) search path
   (the only Shopify-native path that filters the full catalog — see
   docs/COLLECTION-FILTERING-LIMITS.md).
   Spec: docs/superpowers/specs/2026-07-17-singles-browser-design.md.
   Pure helpers are module.exports-guarded so `node --test` can import them. */
(function () {
  'use strict';

  const API_VERSION = '2026-04';
  const PAGE_SIZE = 48;

  // HTML sizes grammar (NOT CSS range syntax — separate parser, and
  // pre-16.4 Safari drops range conditions here). Approximation: real
  // card width varies 160-390px with density; browsers pick the
  // nearest-larger srcset candidate. QA-tunable.
  const CARD_IMG_SIZES = '(min-width: 992px) 300px, 45vw';

  // User text is spliced into a query language where " : ( ) * - and
  // AND/OR/NOT are operators. Keep word chars, whitespace and apostrophes.
  function escapeQueryText(text) {
    return String(text == null ? '' : text)
      .replace(/[^\p{L}\p{N}_\s'’]/gu, ' ')
      .replace(/\b(AND|OR|NOT)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function quoteTag(tag) {
    return 'tag:"' + String(tag).replace(/["\\]/g, '') + '"';
  }

  function handleize(s) {
    return String(s == null ? '' : s).toLowerCase().trim()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Set scope = the single baseQuery tag clause naming a known set tag.
  // Zero known set tags (game parents; a set stamped after the last regen)
  // or 2+ (ambiguous) -> null -> no pruning. Structural tag clauses like
  // tag:"Riftbound" are not in setTags and never count.
  function extractSetScope(baseQuery, setTags) {
    if (!setTags || !setTags.length) return null;
    const re = /tag:"((?:[^"\\]|\\.)*)"/g;
    const hits = [];
    let m;
    while ((m = re.exec(String(baseQuery || ''))) !== null) {
      if (setTags.indexOf(m[1]) !== -1 && hits.indexOf(m[1]) === -1) hits.push(m[1]);
    }
    return hits.length === 1 ? hits[0] : null;
  }

  // Normalises config filter defs to {key,label,tags[]} values. Accepts the
  // legacy {label,tag} form. Per-value leniency lives HERE (skip + warn);
  // top-level shape validation stays all-or-nothing in validateConfig.
  function normalizeFilterDefs(filters, warnFn, setScope) {
    const warn = warnFn || function () {};
    const out = [];
    (filters || []).forEach(function (def) {
      const seen = Object.create(null);
      const values = [];
      (def.values || []).forEach(function (v, i) {
        if (!v || typeof v.label !== 'string' || !v.label.trim()) {
          warn('skipping value ' + i + ' in "' + def.key + '": missing label');
          return;
        }
        let tags = Array.isArray(v.tags) ? v.tags : (v.tag != null ? [v.tag] : []);
        tags = tags.filter(function (t) { return typeof t === 'string' && !!t.trim(); });
        if (!tags.length) {
          warn('skipping value "' + v.label + '" in "' + def.key + '": no usable tags');
          return;
        }
        // Per-set pruning: silent by design — an out-of-set value is expected
        // config data, not a config error.
        if (setScope && Array.isArray(v.sets) && v.sets.indexOf(setScope) === -1) return;
        const key = (typeof v.key === 'string' && v.key.trim()) ? v.key.trim() : handleize(v.label);
        if (seen[key]) {
          warn('skipping value "' + v.label + '" in "' + def.key + '": duplicate key "' + key + '"');
          return;
        }
        seen[key] = true;
        values.push({ key: key, label: v.label, tags: tags });
      });
      if (!values.length) {
        warn('dropping filter group "' + def.key + '": no valid values');
        return;
      }
      out.push({ key: def.key, label: def.label, searchable: !!def.searchable, values: values });
    });
    return out;
  }

  function narrowValues(values, query) {
    const q = String(query == null ? '' : query).trim().toLowerCase();
    return (values || []).filter(function (v) {
      return !q || v.label.toLowerCase().indexOf(q) !== -1;
    }).map(function (v) { return v.key; });
  }

  function buildQuery(baseQuery, filterDefs, state) {
    const parts = ['(' + baseQuery + ')'];
    (filterDefs || []).forEach(function (def) {
      const selected = (state.groups && state.groups[def.key]) || [];
      const byKey = Object.create(null);
      def.values.forEach(function (v) { byKey[v.key] = v; });
      const tags = [];
      selected.forEach(function (k) {
        if (!byKey[k]) return; // unknown key (URL tampering) — dropped before interpolation
        byKey[k].tags.forEach(function (t) { if (tags.indexOf(t) === -1) tags.push(t); });
      });
      if (!tags.length) return;
      const clause = tags.map(quoteTag).join(' OR ');
      parts.push(tags.length > 1 ? '(' + clause + ')' : clause);
    });
    if (state.instock !== false) parts.push('available_for_sale:true');
    if (state.priceMin != null) parts.push('variants.price:>=' + state.priceMin);
    if (state.priceMax != null) parts.push('variants.price:<=' + state.priceMax);
    const text = escapeQueryText(state.q);
    if (text) {
      text.split(' ').forEach(function (term) { parts.push('title:' + term + '*'); });
    }
    return parts.join(' AND ');
  }

  const SORT_OPTIONS = [
    { key: 'best',       label: 'Best selling',       sortKey: 'BEST_SELLING', reverse: false },
    { key: 'price-asc',  label: 'Price: low to high', sortKey: 'PRICE',        reverse: false },
    { key: 'price-desc', label: 'Price: high to low', sortKey: 'PRICE',        reverse: true  },
    { key: 'newest',     label: 'Newest',             sortKey: 'CREATED_AT',   reverse: true  },
    { key: 'title-asc',  label: 'Name: A to Z',       sortKey: 'TITLE',        reverse: false }
  ];
  const DEFAULT_SORT = 'best';

  function parsePrice(raw) {
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return isFinite(n) && n >= 0 ? n : null;
  }

  function parseState(search, filterDefs) {
    const params = new URLSearchParams(search || '');
    const state = {
      q: params.get('q') || '',
      instock: params.get('instock') !== '0',
      priceMin: parsePrice(params.get('pmin')),
      priceMax: parsePrice(params.get('pmax')),
      sort: DEFAULT_SORT,
      groups: {}
    };
    const sort = params.get('sort');
    if (SORT_OPTIONS.some(function (o) { return o.key === sort; })) state.sort = sort;
    (filterDefs || []).forEach(function (def) {
      const raw = params.get(def.key);
      if (!raw) return;
      // Dedupe + drop tampered/unknown value-keys at parse time so no
      // ghost chip renders (buildQuery already drops them at query time).
      const seen = Object.create(null);
      const sel = raw.split(',').filter(function (k) {
        if (!k || seen[k]) return false;
        seen[k] = true;
        return def.values.some(function (v) { return v.key === k; });
      });
      if (sel.length) state.groups[def.key] = sel;
    });
    return state;
  }

  function serializeState(state, filterDefs) {
    const params = new URLSearchParams();
    if (state.q) params.set('q', state.q);
    if (state.instock === false) params.set('instock', '0');
    if (state.priceMin != null) params.set('pmin', String(state.priceMin));
    if (state.priceMax != null) params.set('pmax', String(state.priceMax));
    if (state.sort && state.sort !== DEFAULT_SORT) params.set('sort', state.sort);
    (filterDefs || []).forEach(function (def) {
      const sel = (state.groups && state.groups[def.key]) || [];
      if (sel.length) params.set(def.key, sel.join(','));
    });
    const s = params.toString();
    return s ? '?' + s : '';
  }

  // Customer-applied filters only: group selections, search text, price
  // bounds. The in-stock toggle never counts (its default is on, and turning
  // it off only widens results); sort never counts. Drives the empty-state
  // wording: "your filters emptied this" vs "this collection is empty".
  function hasActiveFilters(state) {
    if (!state) return false;
    const groups = state.groups || {};
    let any = false;
    Object.keys(groups).forEach(function (k) {
      if ((groups[k] || []).length) any = true;
    });
    return any || !!(state.q && state.q.trim()) ||
      state.priceMin != null || state.priceMax != null;
  }

  // Card-level "from" price honouring the customer's price bounds: min
  // price over AVAILABLE variants within [priceMin, priceMax] (null
  // bound = unbounded). Deliberately spans ALL finishes, matching
  // card.fromPrice's semantics. Null when none qualify.
  function fromPriceInRange(variants, priceMin, priceMax) {
    let min = null;
    (variants || []).forEach(function (v) {
      if (!v || !v.available) return;
      const p = Number(v.price);
      if (!isFinite(p)) return;
      if (priceMin != null && p < priceMin) return;
      if (priceMax != null && p > priceMax) return;
      if (min == null || p < min) min = p;
    });
    return min;
  }

  // Sold-out race (cart add 422): flip the variant off and recompute the
  // card's derived fields. Mutates in place; returns the card (null when
  // the variant isn't in any card). Self-heals on the next refetch.
  function markVariantUnavailable(cards, variantId) {
    const idStr = String(variantId);
    let found = null;
    (cards || []).forEach(function (card) {
      if (found) return;
      (card.variants || []).forEach(function (v) {
        if (String(v.id) === idStr) {
          v.available = false;
          found = card;
        }
      });
    });
    if (!found) return null;
    const inStock = found.variants.filter(function (v) { return v.available; });
    found.hasStock = inStock.length > 0;
    found.fromPrice = inStock.length
      ? Math.min.apply(null, inStock.map(function (v) { return v.price; }))
      : null;
    return found;
  }

  function chipButtonsHtml(filterDefs, state) {
    const chips = [];
    (filterDefs || []).forEach(function (def) {
      ((((state || {}).groups) || {})[def.key] || []).forEach(function (k) {
        const val = def.values.filter(function (v) { return v.key === k; })[0];
        chips.push('<button type="button" class="sb-chip" data-sb-chip-group="' + escapeHtml(def.key) +
          '" data-sb-chip-key="' + escapeHtml(k) + '">' + escapeHtml(val ? val.label : k) + ' ✕</button>');
      });
    });
    return chips.join('');
  }

  function emptyStateHtml(state, filterDefs) {
    if (!hasActiveFilters(state)) {
      return '<div class="sb__empty sb__empty--bare">' +
        '<p class="sb__empty-title">No cards in this collection yet.</p></div>';
    }
    const chips = chipButtonsHtml(filterDefs, state);
    return '<div class="sb__empty">' +
      '<p class="sb__empty-title">No cards in this collection match your filters</p>' +
      (chips ? '<div class="sb__empty-chips">' + chips + '</div>' : '') +
      '<button type="button" class="sb-chip sb-chip--clear" data-sb-clear-all>Clear all filters</button>' +
      '</div>';
  }

  function numericId(gid) {
    return String(gid).split('/').pop();
  }

  function parseVariantTitle(title, conditions) {
    if (title === 'Default Title') return { condition: null, label: null, finish: '' };
    for (let i = 0; i < conditions.length; i++) {
      const c = conditions[i];
      if (title === c.title) return { condition: c.title, label: c.label, finish: '' };
      if (title.indexOf(c.title + ' ') === 0) {
        return { condition: c.title, label: c.label, finish: title.slice(c.title.length + 1) };
      }
    }
    // The app's rename retires the condition dimension on MTG singles at target
    // shape: "Near Mint" -> "Non-Foil", "Near Mint Foil" -> "Foil". Both
    // vocabularies coexist permanently (app spec RD6), so this sits AFTER the
    // configured conditions — a game's config always wins.
    //
    // Only "Foil" needs an arm. "Non-Foil" reaches the same answer through the
    // fallthrough below, and must: an endsWith or substring test for "foil"
    // would classify it as a foil, which is the exact bug the app fixed in
    // optionValueFinish. The label stays the raw title rather than null,
    // because null is what `singleVariant` tests for — nulling it would send a
    // product whose only variant is "Foil" down the single-variant path and
    // drop the pill that carries the word.
    if (title === 'Foil') return { condition: null, label: title, finish: 'Foil' };
    return { condition: null, label: title, finish: '' };
  }

  function adaptProducts(connection, conditions) {
    const pageInfo = connection.pageInfo || {};
    const cards = (connection.nodes || []).map(function (node) {
      const variants = ((node.variants && node.variants.nodes) || []).map(function (v) {
        const parsed = parseVariantTitle(v.title, conditions || []);
        return {
          id: numericId(v.id),
          title: v.title,
          condition: parsed.condition,
          label: parsed.label,
          finish: parsed.finish,
          price: Number(v.price && v.price.amount),
          available: !!v.availableForSale
        };
      });
      const finishes = [];
      variants.forEach(function (v) {
        if (finishes.indexOf(v.finish) === -1) finishes.push(v.finish);
      });
      const inStock = variants.filter(function (v) { return v.available; });
      const fi = node.featuredImage;
      const srcsetParts = [];
      if (fi) {
        if (fi.url) srcsetParts.push(fi.url + ' 360w');
        if (fi.url540) srcsetParts.push(fi.url540 + ' 540w');
        if (fi.url720) srcsetParts.push(fi.url720 + ' 720w');
      }
      return {
        handle: node.handle,
        title: node.title,
        url: '/products/' + node.handle,
        image: node.featuredImage ? node.featuredImage.url : null,
        imageAlt: (node.featuredImage && node.featuredImage.altText) || node.title,
        imageSrcset: srcsetParts.length ? srcsetParts.join(', ') : null,
        currency: node.priceRange.minVariantPrice.currencyCode,
        fromPrice: inStock.length
          ? Math.min.apply(null, inStock.map(function (v) { return v.price; }))
          : null,
        hasStock: inStock.length > 0,
        singleVariant: variants.length === 1 && variants[0].condition === null && variants[0].label === null,
        variantOverflow: !!(node.variants && node.variants.pageInfo && node.variants.pageInfo.hasNextPage),
        finishes: finishes,
        variants: variants
      };
    });
    return { hasNextPage: !!pageInfo.hasNextPage, endCursor: pageInfo.endCursor || null, cards: cards };
  }

  function formatMoney(amount, currency) {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || 'AUD' })
      .format(amount);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  const PRODUCTS_QUERY = [
    'query Browse($q: String!, $first: Int!, $after: String, $sortKey: ProductSortKeys!, $reverse: Boolean!) {',
    '  products(first: $first, after: $after, query: $q, sortKey: $sortKey, reverse: $reverse) {',
    '    pageInfo { hasNextPage endCursor }',
    '    nodes {',
    '      handle title',
    '      featuredImage { url(transform: { maxWidth: 360 }) url540: url(transform: { maxWidth: 540 }) url720: url(transform: { maxWidth: 720 }) altText }',
    '      priceRange { minVariantPrice { amount currencyCode } }',
    '      variants(first: 12) {',
    '        pageInfo { hasNextPage }',
    '        nodes { id title availableForSale price { amount } }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function validateConfig(browseConfig, games) {
    if (!browseConfig || typeof browseConfig !== 'object') return { ok: false, reason: 'missing browse config' };
    if (typeof browseConfig.baseQuery !== 'string' || !browseConfig.baseQuery.trim()) {
      return { ok: false, reason: 'missing baseQuery' };
    }
    const game = games && games[browseConfig.game];
    if (!game) return { ok: false, reason: 'unknown game: ' + browseConfig.game };
    if (!Array.isArray(game.conditions)) return { ok: false, reason: 'game conditions missing' };
    if (!Array.isArray(game.filters)) return { ok: false, reason: 'game filters missing' };
    for (let i = 0; i < game.filters.length; i++) {
      const f = game.filters[i];
      if (!f || typeof f.key !== 'string' || typeof f.label !== 'string' || !Array.isArray(f.values)) {
        return { ok: false, reason: 'bad filter def at index ' + i };
      }
    }
    return { ok: true };
  }

  function buildRequestBody(baseQuery, filterDefs, state, cursor) {
    let sort = SORT_OPTIONS[0];
    SORT_OPTIONS.forEach(function (o) { if (o.key === state.sort) sort = o; });
    return {
      query: PRODUCTS_QUERY,
      variables: {
        q: buildQuery(baseQuery, filterDefs, state),
        first: PAGE_SIZE,
        after: cursor || null,
        sortKey: sort.sortKey,
        reverse: sort.reverse
      }
    };
  }

  /* ---------- items-per-row (density) ---------- */

  // Per-breakpoint density preference. Single source of truth for the
  // control's options, defaults and storage keys (night-mode convention).
  const COLS_OPTIONS = { desktop: [3, 4, 5, 6], mobile: [1, 2, 3] };
  const COLS_DEFAULTS = { desktop: 5, mobile: 2 };
  const COLS_STORAGE_KEYS = { desktop: 'tgd-sb-cols-desktop', mobile: 'tgd-sb-cols-mobile' };
  // 160 is deliberately below the auto-fill rule's 180px floor: an explicit
  // "more columns" pick may pack slightly tighter than the automatic layout.
  const COLS_MIN_CARD_WIDTH = 160;
  const COLS_GAP = 16;

  function maxColsForWidth(width, minCardWidth, gap) {
    const w = Number(width);
    if (!isFinite(w) || w <= 0) return 1;
    return Math.max(1, Math.floor((w + gap) / (minCardWidth + gap)));
  }

  function effectiveCols(pref, maxFit) {
    const m = Number(maxFit);
    const safeMax = isFinite(m) && m >= 1 ? Math.floor(m) : 1;
    const p = Number(pref);
    if (!isFinite(p) || p < 1) return safeMax;
    return Math.min(Math.floor(p), safeMax);
  }

  function parseColsPref(raw, allowedValues, fallback) {
    if (raw == null || raw === '') return fallback;
    const n = Number(raw);
    return (allowedValues || []).indexOf(n) !== -1 ? n : fallback;
  }

  /* ---------- polish-batch pure helpers ---------- */

  // Badge on the mobile Filters button: counts what the SHEET manages —
  // group selections + price (as one). Deliberately narrower than
  // hasActiveFilters: q / instock / sort never count.
  function activeFilterCount(state) {
    if (!state) return 0;
    let count = 0;
    const groups = state.groups || {};
    Object.keys(groups).forEach(function (k) { count += (groups[k] || []).length; });
    if (state.priceMin != null || state.priceMax != null) count += 1;
    return count;
  }

  function doneLabel(count, hasNextPage) {
    const c = Number(count);
    const n = isFinite(c) && c >= 0 ? Math.floor(c) : 0;
    const plus = hasNextPage ? '+' : '';
    return 'Show ' + n + plus + ' result' + (n === 1 && !hasNextPage ? '' : 's');
  }

  // Inert placeholders: aria-hidden, no data-sb-card, so the grid click
  // handler and screen readers both ignore them.
  function skeletonCardsHtml(count) {
    const c = Number(count);
    if (!isFinite(c) || c <= 0) return '';
    const n = Math.floor(c);
    let html = '';
    for (let i = 0; i < n; i++) {
      html += '<li class="sb-card sb-card--skel" aria-hidden="true">' +
        '<div class="sb-card__media sb-skel__media"></div>' +
        '<div class="sb-skel__bar"></div>' +
        '<div class="sb-skel__bar sb-skel__bar--short"></div></li>';
    }
    return html;
  }

  /* ---------- hybrid infinite scroll ---------- */

  // Auto-loads allowed per filter state; beyond this the Load more
  // button takes over (footer stays reachable, DOM stays bounded).
  const AUTO_LOAD_PAGES = 3;
  // px of IntersectionObserver lookahead below the viewport.
  const SENTINEL_LOOKAHEAD = 600;

  // A request made PAST the end of the list returns `nodes: []`,
  // `hasNextPage: false` and `endCursor: null` (measured against the
  // Storefront API, 2026-09-10). Assigning that null over a good cursor makes
  // the NEXT request `after: null` — byte-identical to the session's first
  // request — which `fetchPage` serves from its variables-keyed cache as page
  // ONE, re-appending the whole list and re-arming `hasNextPage`. That is the
  // load-more loop: the grid grows forever while the distinct count never
  // moves. Keep the last good cursor on an append; a replace legitimately
  // resets it, including to null for an empty result set.
  function nextCursor(current, page, replace) {
    if (replace) return page.endCursor;
    return page.endCursor === null ? current : page.endCursor;
  }

  function shouldAutoLoad(autoLoads, maxAutoLoads, hasNextPage, busy) {
    if (!hasNextPage || busy) return false;
    const n = Number(autoLoads);
    if (!isFinite(n)) return false;
    return n < maxAutoLoads;
  }

  /* ---------- DOM component (browser only) ---------- */
  if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined' && window.customElements) {

    const warn = function (msg) { console.warn('[singles-browser] ' + msg); };

    // QA-tunable: px between viewport top and the component after a
    // scroll-to-results (fixed-header allowance; no --header-height var
    // exists and the header differs by breakpoint — live QA decides).
    const SCROLL_HEADER_OFFSET = 80;

    const readJson = function (el) {
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    };

    class SinglesBrowserElement extends HTMLElement {
      connectedCallback() {
        if (this._booted) return;
        this._booted = true;
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.boot.bind(this));
        } else {
          this.boot();
        }
      }

      boot() {
        const games = readJson(document.getElementById('singles-browser-games'));
        let browseConfig = readJson(this.querySelector('script[data-sb-config]'));
        // The json filter on a JSON-string metafield can double-encode; unwrap once.
        if (typeof browseConfig === 'string') { try { browseConfig = JSON.parse(browseConfig); } catch (e) { browseConfig = null; } }
        const token = window.theme && window.theme.storefront && window.theme.storefront.token;
        if (!token) return warn('no storefront token configured');
        const v = validateConfig(browseConfig, games);
        if (!v.ok) return warn(v.reason);

        this.token = token;
        this.apiVersion = (window.theme.storefront && window.theme.storefront.apiVersion) || API_VERSION;
        this.baseQuery = browseConfig.baseQuery;
        this.game = games[browseConfig.game];
        this.filterDefs = normalizeFilterDefs(
          this.game.filters, warn,
          extractSetScope(browseConfig.baseQuery, this.game.setTags)
        );
        this.conditions = this.game.conditions;
        this.state = parseState(window.location.search, this.filterDefs);
        this._mqDesktop = window.matchMedia('(min-width: 992px)');
        this.cols = {
          desktop: this.readColsPref('desktop'),
          mobile: this.readColsPref('mobile')
        };
        this.cache = Object.create(null);
        this.cards = [];
        this.cursor = null;
        this.hasNextPage = false;
        // Stale responses from superseded filter states must never render.
        this._reqId = 0;
        this._autoLoads = 0;
        this._lastNavSearch = false;
        this._resetSkelsShown = false;

        const self = this;
        this.fetchPage(null).then(function (page) {
          self.activate();
          self.applyPage(page, true);
        }).catch(function (err) {
          warn('initial fetch failed, leaving legacy grid in place: ' + err.message);
        });
      }

      readColsPref(bp) {
        let raw = null;
        try { raw = window.localStorage.getItem(COLS_STORAGE_KEYS[bp]); } catch (e) {}
        return parseColsPref(raw, COLS_OPTIONS[bp], COLS_DEFAULTS[bp]);
      }

      fetchPage(cursor) {
        const body = buildRequestBody(this.baseQuery, this.filterDefs, this.state, cursor);
        const key = JSON.stringify(body.variables);
        if (this.cache[key]) return Promise.resolve(this.cache[key]);
        const self = this;
        return fetch('/api/' + this.apiVersion + '/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.token
          },
          body: JSON.stringify(body)
        }).then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        }).then(function (json) {
          if (json.errors) throw new Error((json.errors[0] && json.errors[0].message) || 'GraphQL error');
          const page = adaptProducts(json.data.products, self.conditions);
          if (page.cards.some(function (c) { return c.variantOverflow; })) {
            warn('a product exceeded the variants(first: 12) window');
          }
          self.cache[key] = page;
          return page;
        });
      }

      activate() {
        if (this._active) return;
        this._active = true;

        // Neutralise the legacy filter AJAX. Both <collection-filters-form>
        // instances bound a window-level popstate in their constructor; our
        // pushState navigation would otherwise make the first Back press
        // rewrite #CollectionProductGrid and destroy this component.
        const patch = function (Legacy) {
          if (!Legacy) return;
          Legacy.prototype.renderPage = function () {};
          Legacy.prototype.onHistoryChange = function () {};
        };
        patch(window.customElements.get('collection-filters-form'));
        window.customElements.whenDefined('collection-filters-form').then(function () {
          patch(window.customElements.get('collection-filters-form'));
        }).catch(function () {});

        // Hides legacy grid/filters/sidebar and widens the layout — CSS in
        // singles-browser.css keys off this class. Liquid rendered them all,
        // so the no-JS page is untouched.
        document.body.classList.add('singles-browser-active');
        this.removeAttribute('hidden');

        this.renderShell();
        window.addEventListener('popstate', this.onPopstate.bind(this));

        const self = this;
        let colsResizeTimer;
        window.addEventListener('resize', function () {
          clearTimeout(colsResizeTimer);
          colsResizeTimer = setTimeout(function () {
            self.applyCols();
            // Header height changes across breakpoints/orientation.
            self.syncStickyTop();
          }, 150);
        });

        // Outside click / Esc close the dropdowns (document-level, same
        // placement as the display-prefs panel's listeners in custom.js).
        // toolbarEl persists across rebuilds, so live queries stay valid.
        document.addEventListener('click', function (e) {
          if (self.toolbarEl.contains(e.target)) return;
          Array.prototype.forEach.call(self.toolbarEl.querySelectorAll('details.sb-filter[open]'), function (d) {
            d.open = false;
          });
        });
        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Escape') return;
          // Sheet first: mobile-only overlay; early return keeps the
          // dropdown handling below deterministic.
          if (self.sheetEl && self.sheetEl.classList.contains('is-open')) {
            self.closeSheet();
            return;
          }
          const open = self.toolbarEl.querySelector('details.sb-filter[open]');
          if (!open) return;
          const hadFocus = open.contains(document.activeElement);
          open.open = false;
          // Return focus to the trigger only if focus was inside the
          // dropdown — never steal the caret from e.g. the search input.
          if (hadFocus) {
            const summary = open.querySelector('summary');
            if (summary) summary.focus();
          }
        });
      }

      onPopstate() {
        this._lastNavSearch = false;
        this.state = parseState(window.location.search, this.filterDefs);
        this.renderToolbar();
        this.renderChips();
        this.renderSheetContent();
        this.resetAndFetch(true);
      }

      renderShell() {
        this.innerHTML =
          '<div class="sb">' +
          '  <div class="sb__sentinel" data-sb-sentinel-top aria-hidden="true"></div>' +
          '  <div class="sb__toolbar" data-sb-toolbar></div>' +
          '  <div class="sb__chips" data-sb-chips></div>' +
          '  <ul class="sb__grid" data-sb-grid></ul>' +
          '  <div class="sb__sentinel" data-sb-sentinel-bottom aria-hidden="true"></div>' +
          '  <div class="sb__status" data-sb-status></div>' +
          '  <button type="button" class="sb__load-more" data-sb-more hidden>Load more</button>' +
          '</div>';
        this.toolbarEl = this.querySelector('[data-sb-toolbar]');
        this.chipsEl = this.querySelector('[data-sb-chips]');
        this.gridEl = this.querySelector('[data-sb-grid]');
        this.statusEl = this.querySelector('[data-sb-status]');
        this.moreBtn = this.querySelector('[data-sb-more]');

        this.applyCols();

        // Fallback until the fixed header can be measured (it only exists
        // once shop.js adds .fixed-header on scroll — see syncStickyTop).
        this._stickyTop = SCROLL_HEADER_OFFSET;
        this.querySelector('.sb').style.setProperty('--sb-sticky-top', this._stickyTop + 'px');
        this.armStuckObserver();

        const self = this;

        this.moreBtn.addEventListener('click', function () { self.loadMore(); });

        this._bottomSentinel = this.querySelector('[data-sb-sentinel-bottom]');
        this._bottomObserver = new IntersectionObserver(function (entries) {
          if (!entries[entries.length - 1].isIntersecting) return;
          if (!shouldAutoLoad(self._autoLoads, AUTO_LOAD_PAGES, self.hasNextPage,
              self.moreBtn.disabled || self.resetBusy())) return;
          self._autoLoads++;
          self.loadMore();
        }, { rootMargin: '0px 0px ' + SENTINEL_LOOKAHEAD + 'px 0px', threshold: 0 });
        this._bottomObserver.observe(this._bottomSentinel);

        this.renderToolbar();
        this.renderChips();
        this.buildSheet();
        this.bindStateEvents();
      }

      // One delegated handler serves both the desktop dropdowns and the
      // mobile sheet: every filter checkbox carries data-sb-group/data-sb-key.
      bindStateEvents() {
        const self = this;
        const onChange = function (e) {
          const t = e.target;
          if (t.matches('[data-sb-group]')) {
            const key = t.getAttribute('data-sb-group');
            const valKey = t.getAttribute('data-sb-key');
            const sel = (self.state.groups[key] || []).slice();
            const idx = sel.indexOf(valKey);
            if (t.checked && idx === -1) sel.push(valKey);
            if (!t.checked && idx !== -1) sel.splice(idx, 1);
            if (sel.length) self.state.groups[key] = sel; else delete self.state.groups[key];
            self.setState({});
          } else if (t.matches('[data-sb-instock]')) {
            self.setState({ instock: t.checked });
          } else if (t.matches('[data-sb-sort]')) {
            self.setState({ sort: t.value });
          } else if (t.matches('[data-sb-pmin]')) {
            self.setState({ priceMin: parsePrice(t.value) });
          } else if (t.matches('[data-sb-pmax]')) {
            self.setState({ priceMax: parsePrice(t.value) });
          }
        };
        this.addEventListener('change', onChange);
        this.sheetEl.addEventListener('change', onChange);

        let debounceTimer;
        this.addEventListener('input', function (e) {
          if (!e.target.matches('[data-sb-q]')) return;
          clearTimeout(debounceTimer);
          const value = e.target.value;
          debounceTimer = setTimeout(function () { self.setState({ q: value }, { skipToolbar: true, coalesceHistory: true }); }, 400);
        });

        const onNarrowInput = function (e) {
          if (!e.target.matches('[data-sb-narrow]')) return;
          self.applyNarrow(e.target);
        };
        this.addEventListener('input', onNarrowInput);
        this.sheetEl.addEventListener('input', onNarrowInput);

        const onClick = function (e) {
          const colsBtn = e.target.closest('[data-sb-cols]');
          if (colsBtn) {
            self.setCols(colsBtn.getAttribute('data-sb-cols-bp'),
              Number(colsBtn.getAttribute('data-sb-cols')), colsBtn);
            return;
          }
          const chip = e.target.closest('[data-sb-chip-group]');
          if (chip) {
            const key = chip.getAttribute('data-sb-chip-group');
            const valKey = chip.getAttribute('data-sb-chip-key');
            const sel = (self.state.groups[key] || []).filter(function (k2) { return k2 !== valKey; });
            if (sel.length) self.state.groups[key] = sel; else delete self.state.groups[key];
            self.setState({});
            return;
          }
          if (e.target.closest('[data-sb-clear-all]')) {
            // Blank type-ahead narrows BEFORE setState: the rebuild's
            // captureNarrows snapshot would otherwise resurrect them.
            const clearNarrows = function (root) {
              Array.prototype.forEach.call(root.querySelectorAll('[data-sb-narrow]'), function (inp) { inp.value = ''; });
            };
            clearNarrows(self.toolbarEl);
            if (self.sheetEl) clearNarrows(self.sheetEl);
            self.state = parseState('', self.filterDefs);
            self.setState({});
            return;
          }
          if (e.target.closest('[data-sb-open-sheet]')) self.openSheet();
        };
        this.addEventListener('click', onClick);
        this.sheetEl.addEventListener('click', onClick);

        this.gridEl.addEventListener('click', function (e) {
          const li = e.target.closest('[data-sb-card]');
          if (!li) return;
          const card = self.cards.filter(function (c) { return c.handle === li.getAttribute('data-sb-card'); })[0];
          if (!card) return;

          const finishBtn = e.target.closest('[data-sb-finish]');
          if (finishBtn) {
            li.setAttribute('data-sb-sel-finish', finishBtn.getAttribute('data-sb-finish'));
            li.innerHTML = self.renderCardInner(card, finishBtn.getAttribute('data-sb-finish'), null);
            return;
          }
          const pill = e.target.closest('[data-sb-pill]');
          if (pill && !pill.disabled) {
            li.innerHTML = self.renderCardInner(card, li.getAttribute('data-sb-sel-finish'), pill.getAttribute('data-sb-pill'));
            return;
          }
          const add = e.target.closest('[data-sb-add]');
          if (add) self.addToCart(add.getAttribute('data-sb-add'), add);
        });

        // One dropdown open at a time. toggle doesn't bubble, but
        // non-bubbling events still traverse the capture phase. Guard on
        // open === true: closing siblings below fires their own
        // close-toggles into this same handler.
        this.addEventListener('toggle', function (e) {
          const d = e.target;
          if (!(d instanceof HTMLElement) || !d.matches('details.sb-filter') || !d.open) return;
          Array.prototype.forEach.call(self.toolbarEl.querySelectorAll('details.sb-filter[open]'), function (other) {
            if (other !== d) other.open = false;
          });
        }, true);

        // Roving radio keyboard: arrows move + select within a pill
        // group, wrapping, skipping greyed (disabled) pills. Selection
        // follows focus — safe, setCols is presentation-only.
        this.addEventListener('keydown', function (e) {
          const pill = e.target.closest ? e.target.closest('.sb__cols-pill') : null;
          if (!pill) return;
          let dir = 0;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') dir = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
          if (!dir) return;
          // Never swallow browser shortcuts (e.g. Alt+Left = Back).
          if (e.altKey || e.ctrlKey || e.metaKey) return;
          e.preventDefault();
          const group = pill.closest('[role="radiogroup"]');
          if (!group) return;
          const pills = Array.prototype.filter.call(
            group.querySelectorAll('[data-sb-cols]'),
            function (b) { return !b.disabled; });
          const idx = pills.indexOf(pill);
          if (idx === -1 || pills.length < 2) return;
          const next = pills[(idx + dir + pills.length) % pills.length];
          next.focus();
          self.setCols(next.getAttribute('data-sb-cols-bp'), Number(next.getAttribute('data-sb-cols')), next);
        });
      }

      setState(partial, opts) {
        const self = this;
        Object.keys(partial).forEach(function (k) { self.state[k] = partial[k]; });
        const qs = serializeState(this.state, this.filterDefs);
        // Coalesce debounced search bursts: the first pause pushes ONE
        // entry (Back returns to the pre-search state), later pauses
        // replace it. Any other change re-arms the next burst to push.
        if (opts && opts.coalesceHistory && this._lastNavSearch) {
          history.replaceState({ sb: true }, '', window.location.pathname + qs);
        } else {
          history.pushState({ sb: true }, '', window.location.pathname + qs);
        }
        this._lastNavSearch = !!(opts && opts.coalesceHistory);
        // Skip toolbar rebuild for text changes: it would destroy the search input mid-typing (focus/caret loss).
        if (!(opts && opts.skipToolbar)) this.renderToolbar();
        this.renderChips();
        this.renderSheetContent();
        // Search typing (skipToolbar's only caller) must not yank scroll.
        this.resetAndFetch(!!(opts && opts.skipToolbar));
      }

      filterListHtml() {
        const self = this;
        return this.filterDefs.map(function (def) {
          const sel = self.state.groups[def.key] || [];
          const items = def.values.map(function (v) {
            const checked = sel.indexOf(v.key) !== -1 ? ' checked' : '';
            return '<label class="sb-filter__option" data-sb-option="' + escapeHtml(v.key) + '">' +
              '<input type="checkbox"' + checked +
              ' data-sb-group="' + escapeHtml(def.key) + '" data-sb-key="' + escapeHtml(v.key) + '">' +
              '<span>' + escapeHtml(v.label) + '</span></label>';
          }).join('');
          return { def: def, sel: sel, items: items };
        });
      }

      narrowHtml(def) {
        if (!def.searchable) return '';
        return '<input type="search" class="sb-filter__narrow" placeholder="Type to filter…"' +
          ' data-sb-narrow="' + escapeHtml(def.key) + '" aria-label="Filter ' + escapeHtml(def.label) + '">' +
          '<div class="sb-filter__nomatch" role="status" hidden>No matches</div>';
      }

      colsGroupHtml(bp) {
        const self = this;
        return '<div class="sb__cols sb__cols--' + bp + '" role="radiogroup" aria-label="Cards per row">' +
          COLS_OPTIONS[bp].map(function (n) {
            const active = n === self.cols[bp];
            return '<button type="button" role="radio" class="sb__cols-pill' + (active ? ' is-active' : '') +
              '" aria-checked="' + (active ? 'true' : 'false') +
              '" tabindex="' + (active ? '0' : '-1') +
              '" data-sb-cols="' + n + '" data-sb-cols-bp="' + bp + '">' + n + '</button>';
          }).join('') + '</div>';
      }

      // Presentation-only: no renderToolbar rebuild, no refetch, no history
      // entry. Active class + aria-checked are updated in place; later
      // rebuilds re-emit the same state from this.cols.
      setCols(bp, n, btn) {
        if (!COLS_OPTIONS[bp] || COLS_OPTIONS[bp].indexOf(n) === -1) return;
        this.cols[bp] = n;
        try { window.localStorage.setItem(COLS_STORAGE_KEYS[bp], String(n)); } catch (e) {}
        const group = btn.closest('[role="radiogroup"]');
        if (group) {
          Array.prototype.forEach.call(group.querySelectorAll('[data-sb-cols]'), function (b) {
            const on = b === btn;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
            b.setAttribute('tabindex', on ? '0' : '-1');
          });
        }
        this.applyCols();
      }

      // Clamp = min(pref, what fits). Sets class + var together on the .sb
      // root, which renderShell built once and no rebuild ever replaces.
      applyCols() {
        const root = this.querySelector('.sb');
        if (!root || !this.gridEl) return;
        const bp = this._mqDesktop.matches ? 'desktop' : 'mobile';
        const maxFit = maxColsForWidth(this.gridEl.clientWidth, COLS_MIN_CARD_WIDTH, COLS_GAP);
        this._maxFit = maxFit;
        root.classList.add('sb--cols');
        root.style.setProperty('--sb-cols', String(effectiveCols(this.cols[bp], maxFit)));
        this.syncColsFit();
      }

      // Grey out pills that can't fit the current viewport, and keep each
      // group Tab-reachable: a disabled button can't receive focus, so
      // the roving tabindex entry is the active pill if enabled, else the
      // highest-value ENABLED pill (= the clamped effective count, which
      // always exists for the visible group). A group whose options are
      // all unfittable (the hidden other-breakpoint group on extreme
      // viewports) gets no entry — it is display:none anyway.
      syncColsFit() {
        const maxFit = this._maxFit;
        if (!maxFit) return;
        Array.prototype.forEach.call(this.querySelectorAll('.sb__cols'), function (group) {
          const pills = group.querySelectorAll('[data-sb-cols]');
          let entry = null;
          let bestEnabled = null;
          Array.prototype.forEach.call(pills, function (b) {
            const n = Number(b.getAttribute('data-sb-cols'));
            const unfit = n > maxFit;
            b.disabled = unfit;
            if (unfit) b.setAttribute('aria-disabled', 'true');
            else b.removeAttribute('aria-disabled');
            if (!unfit) {
              if (b.classList.contains('is-active')) entry = b;
              if (bestEnabled === null || n > Number(bestEnabled.getAttribute('data-sb-cols'))) bestEnabled = b;
            }
          });
          if (!entry) entry = bestEnabled;
          Array.prototype.forEach.call(pills, function (b) {
            b.setAttribute('tabindex', b === entry ? '0' : '-1');
          });
        });
      }

      renderToolbar() {
        const self = this;
        const sortOptions = SORT_OPTIONS.map(function (o) {
          return '<option value="' + o.key + '"' + (o.key === self.state.sort ? ' selected' : '') + '>' +
            escapeHtml(o.label) + '</option>';
        }).join('');
        const groups = this.filterListHtml().map(function (g) {
          const count = g.sel.length ? ' <span class="sb-filter__count">' + g.sel.length + '</span>' : '';
          return '<details class="sb-filter"><summary class="sb-filter__button">' +
            escapeHtml(g.def.label) + count + '</summary>' +
            '<div class="sb-filter__panel" data-sb-panel>' + self.narrowHtml(g.def) + g.items + '</div></details>';
        }).join('');

        // Rebuilding closes details; restore open state so multi-select and price entry survive.
        const openIdx = Array.prototype.map.call(this.toolbarEl.querySelectorAll('.sb-filter'), function (d, i) { return d.open ? i : -1; }).filter(function (i) { return i >= 0; });
        const narrowSnap = this.captureNarrows(this.toolbarEl);

        this.toolbarEl.innerHTML =
          '<input class="sb__search" type="search" placeholder="Search cards…" value="' +
            escapeHtml(this.state.q) + '" data-sb-q aria-label="Search cards">' +
          '<div class="sb__filters">' + groups +
          '  <details class="sb-filter"><summary class="sb-filter__button">Price</summary>' +
          '    <div class="sb-filter__panel sb-filter__panel--price">' +
          '      <label>Min <input type="number" min="0" step="1" value="' + (this.state.priceMin == null ? '' : this.state.priceMin) + '" data-sb-pmin></label>' +
          '      <label>Max <input type="number" min="0" step="1" value="' + (this.state.priceMax == null ? '' : this.state.priceMax) + '" data-sb-pmax></label>' +
          '    </div></details>' +
          '</div>' +
          '<label class="sb__instock"><input type="checkbox"' + (this.state.instock ? ' checked' : '') + ' data-sb-instock><span>In stock</span></label>' +
          '<select class="sb__sort" data-sb-sort aria-label="Sort">' + sortOptions + '</select>' +
          this.colsGroupHtml('desktop') +
          this.colsGroupHtml('mobile') +
          '<button type="button" class="sb__open-sheet" data-sb-open-sheet>Filters' +
            (activeFilterCount(this.state) ? ' <span class="sb-filter__count">' + activeFilterCount(this.state) + '</span>' : '') +
          '</button>';

        Array.prototype.forEach.call(this.toolbarEl.querySelectorAll('.sb-filter'), function (d, i) { if (openIdx.indexOf(i) !== -1) d.setAttribute('open', ''); });
        this.restoreNarrows(this.toolbarEl, narrowSnap);
        // Freshly rebuilt pills need fit/tabindex state (idempotent).
        this.applyCols();
      }

      renderChips() {
        const chips = chipButtonsHtml(this.filterDefs, this.state);
        this.chipsEl.innerHTML = chips
          ? chips + '<button type="button" class="sb-chip sb-chip--clear" data-sb-clear-all>Clear all</button>'
          : '';
      }

      // Narrowing hides/shows existing labels — it must never rebuild the
      // panel (innerHTML would eat the input's focus/caret, the same hazard
      // skipToolbar exists for) and never touches filter state.
      applyNarrow(input) {
        const key = input.getAttribute('data-sb-narrow');
        const def = this.filterDefs.filter(function (d) { return d.key === key; })[0];
        const panel = input.closest('[data-sb-panel]');
        if (!def || !panel) return;
        const visible = narrowValues(def.values, input.value);
        let any = false;
        Array.prototype.forEach.call(panel.querySelectorAll('[data-sb-option]'), function (opt) {
          const show = visible.indexOf(opt.getAttribute('data-sb-option')) !== -1;
          opt.hidden = !show;
          if (show) any = true;
        });
        const nomatch = panel.querySelector('.sb-filter__nomatch');
        if (nomatch) nomatch.hidden = any;
      }

      captureNarrows(root) {
        const snap = { values: {}, focusKey: null, caret: 0 };
        Array.prototype.forEach.call(root.querySelectorAll('[data-sb-narrow]'), function (inp) {
          const k = inp.getAttribute('data-sb-narrow');
          if (inp.value) snap.values[k] = inp.value;
          if (inp === document.activeElement) {
            snap.focusKey = k;
            snap.caret = inp.selectionStart == null ? inp.value.length : inp.selectionStart;
          }
        });
        return snap;
      }

      restoreNarrows(root, snap) {
        const self = this;
        Array.prototype.forEach.call(root.querySelectorAll('[data-sb-narrow]'), function (inp) {
          const k = inp.getAttribute('data-sb-narrow');
          if (snap.values[k] != null) {
            inp.value = snap.values[k];
            self.applyNarrow(inp);
          }
          if (snap.focusKey === k) {
            inp.focus();
            try { inp.setSelectionRange(snap.caret, snap.caret); } catch (e) {}
          }
        });
      }

      // The bottom sheet is position: fixed, so it MUST be a child of
      // document.body — a backdrop-filter ancestor becomes its containing
      // block and clips it (Bug 1 in docs/SORT-FILTERS.md).
      buildSheet() {
        this.sheetEl = document.createElement('div');
        this.sheetEl.className = 'sb-sheet';
        document.body.appendChild(this.sheetEl);
        this.renderSheetContent();
        const self = this;
        this.sheetEl.addEventListener('click', function (e) {
          if (e.target.closest('[data-sb-close-sheet]') || e.target === self.sheetEl) self.closeSheet();
        });
      }

      renderSheetContent() {
        if (!this.sheetEl) return;
        const narrowSnap = this.captureNarrows(this.sheetEl);
        const oldPanel = this.sheetEl.querySelector('.sb-sheet__panel');
        const sheetScroll = oldPanel ? oldPanel.scrollTop : 0;
        const self = this;
        const groups = this.filterListHtml().map(function (g) {
          return '<div class="sb-sheet__group" data-sb-panel><h3>' + escapeHtml(g.def.label) + '</h3>' +
            self.narrowHtml(g.def) + g.items + '</div>';
        }).join('');
        const price =
          '<div class="sb-sheet__group sb-sheet__price"><h3>Price</h3>' +
          '  <label>Min <input type="number" min="0" step="1" value="' + (this.state.priceMin == null ? '' : this.state.priceMin) + '" data-sb-pmin></label>' +
          '  <label>Max <input type="number" min="0" step="1" value="' + (this.state.priceMax == null ? '' : this.state.priceMax) + '" data-sb-pmax></label>' +
          '</div>';
        this.sheetEl.innerHTML =
          '<div class="sb-sheet__panel">' +
          '  <div class="sb-sheet__header"><h2>Filters</h2>' +
          '    <button type="button" class="sb-sheet__close" data-sb-close-sheet aria-label="Close">✕</button></div>' +
          groups + price +
          '  <button type="button" class="sb-sheet__done" data-sb-done data-sb-close-sheet>' +
            escapeHtml(doneLabel(this.cards.length, this.hasNextPage)) + '</button>' +
          '</div>';
        this.restoreNarrows(this.sheetEl, narrowSnap);
        // Rebuild replaced the panel — restore mid-filtering scroll.
        const newPanel = this.sheetEl.querySelector('.sb-sheet__panel');
        if (newPanel) newPanel.scrollTop = sheetScroll;
      }

      openSheet() {
        this._sheetReturnFocus = document.activeElement;
        this.sheetEl.classList.add('is-open');
        const close = this.sheetEl.querySelector('.sb-sheet__close');
        if (close) close.focus();
      }

      closeSheet() {
        this.sheetEl.classList.remove('is-open');
        // Stashed node may be unset or replaced by a toolbar rebuild —
        // fall back to a freshly queried Filters button.
        let target = this._sheetReturnFocus;
        this._sheetReturnFocus = null;
        if (!target || !target.isConnected) target = this.querySelector('.sb__open-sheet');
        if (target && target.focus) target.focus();
      }

      applyPage(page, replace) {
        this.cards = replace ? page.cards.slice() : this.cards.concat(page.cards);
        this.cursor = nextCursor(this.cursor, page, replace);
        this.hasNextPage = page.hasNextPage;
        if (replace) {
          // New filter state: re-arm auto-loading and rebuild in full.
          // The sentinel re-arm makes a reset behave exactly like boot on
          // huge viewports where no intersection crossing would occur.
          this._autoLoads = 0;
          this.renderGrid();
          this.rearmBottomSentinel();
        } else {
          // Append only the new page — existing cards keep their DOM, so
          // finish/pill selections on loaded cards survive a load-more.
          const self = this;
          this.gridEl.insertAdjacentHTML('beforeend',
            page.cards.map(function (c) { return self.cardLiHtml(c); }).join(''));
        }
        this.renderStatus();
        // Re-clamp after content renders: the first page can summon the
        // vertical scrollbar, shrinking clientWidth below what renderShell
        // measured on the empty grid.
        this.applyCols();
      }

      // A reset holds the dim or the skeletons for its entire lifetime
      // (Unit 25 invariant) — together they are the reset-in-flight
      // signal. moreBtn.disabled alone is NOT enough: resetAndFetch never
      // disables the button, and a load-more started after the reset's
      // ++_reqId captures the SAME rid, passes the staleness guard, and
      // would append a stale page (old cursor against the new query).
      resetBusy() {
        return this.classList.contains('sb--loading') ||
          !!this.gridEl.querySelector('.sb-card--skel');
      }

      loadMore() {
        if (this.resetBusy()) return;
        // Nothing left to fetch. Without this the request still goes out, and
        // its null `endCursor` is what starts the loop described on
        // `nextCursor`. `renderStatus` hides the button when `hasNextPage` is
        // false, but the button is not the only caller and a hidden button is
        // still clickable programmatically.
        if (!this.hasNextPage) return;
        const self = this;
        const rid = this._reqId;
        this.moreBtn.disabled = true;
        this.gridEl.insertAdjacentHTML('beforeend', skeletonCardsHtml(this.effectiveColsNow()));
        const removeSkels = function () {
          Array.prototype.forEach.call(self.gridEl.querySelectorAll('.sb-card--skel'), function (li) { li.remove(); });
        };
        this.fetchPage(this.cursor).then(function (page) {
          self.moreBtn.disabled = false;
          if (rid !== self._reqId) return;
          removeSkels();
          self.applyPage(page, false);
          // IntersectionObserver fires on crossings only; if the sentinel
          // stayed visible through the append, force a re-evaluation so
          // short pages chain up to the cap.
          self.rearmBottomSentinel();
        }).catch(function (err) {
          self.moreBtn.disabled = false;
          if (rid !== self._reqId) return;
          removeSkels();
          self.toast('Could not load more: ' + err.message, true);
        });
      }

      rearmBottomSentinel() {
        if (!this._bottomObserver || !this._bottomSentinel) return;
        this._bottomObserver.unobserve(this._bottomSentinel);
        this._bottomObserver.observe(this._bottomSentinel);
      }

      // The base theme's header fixes on scroll (shop.js adds .fixed-header
      // to .header-top / .site-header-mobile) at z-index 51/99 — far above
      // this toolbar (30). The sticky offset must clear the header's REAL
      // height, which is logo-config- and breakpoint-dependent, so it is
      // measured live; SCROLL_HEADER_OFFSET is only the pre-measurement
      // fallback. Never updates while the header is absent (scrolled to
      // top) — the last good measurement stands.
      syncStickyTop() {
        const fh = document.querySelector('.fixed-header');
        if (!fh) return;
        const next = Math.ceil(fh.getBoundingClientRect().height) + 12;
        if (next === this._stickyTop) return;
        this._stickyTop = next;
        const root = this.querySelector('.sb');
        if (root) root.style.setProperty('--sb-sticky-top', next + 'px');
        this.armStuckObserver();
      }

      // Stuck detection: the 1px top sentinel exits the adjusted viewport
      // top exactly when the toolbar pins. Class lives on the persistent
      // toolbarEl, so renderToolbar rebuilds never reset it. Runs on all
      // breakpoints; the class is inert on mobile (every visual rule sits
      // inside the desktop media query). Re-armed whenever _stickyTop
      // changes so the rootMargin tracks the measured offset; the first
      // pin is also the first reliable moment to measure the fixed header
      // (syncStickyTop no-ops back into armStuckObserver only on change,
      // so this cannot recurse).
      armStuckObserver() {
        const self = this;
        const sentinel = this.querySelector('[data-sb-sentinel-top]');
        if (!sentinel) return;
        if (this._stuckObserver) this._stuckObserver.disconnect();
        this._stuckObserver = new IntersectionObserver(function (entries) {
          const stuck = !entries[entries.length - 1].isIntersecting;
          self.toolbarEl.classList.toggle('sb__toolbar--stuck', stuck);
          if (stuck) self.syncStickyTop();
        }, { rootMargin: '-' + this._stickyTop + 'px 0px 0px 0px', threshold: 0 });
        this._stuckObserver.observe(sentinel);
      }

      resetAndFetch(noScroll) {
        const rid = ++this._reqId;
        const self = this;
        // Precision (Unit 28): only RESET-owned skeletons suppress the
        // dim. The old DOM query also matched a load-more skeleton row,
        // wrongly leaving stale cards at full brightness. The error
        // branch below stays DOM-truth — a superseded load-more's skels
        // are cleared only by our renderGrid.
        if (!this._resetSkelsShown) this.classList.add('sb--loading');
        clearTimeout(this._skelTimer);
        // 150ms guard: cache hits resolve first and never flash skeletons;
        // slow fetches swap the dim for skeletons (never both at once).
        this._skelTimer = setTimeout(function () {
          if (rid !== self._reqId) return;
          self.classList.remove('sb--loading');
          self.gridEl.innerHTML = skeletonCardsHtml(self.effectiveColsNow() * 2);
          self._resetSkelsShown = true;
          self.statusEl.textContent = '';
          // Hide Load more under skeletons too (renderStatus restores it
          // from hasNextPage on resolve and on the error path).
          self.moreBtn.hidden = true;
        }, 150);
        this.fetchPage(null).then(function (page) {
          if (rid !== self._reqId) return;
          clearTimeout(self._skelTimer);
          self.classList.remove('sb--loading');
          self.applyPage(page, true);
          if (!noScroll) self.scrollToResults();
        }).catch(function (err) {
          if (rid !== self._reqId) return;
          clearTimeout(self._skelTimer);
          self.classList.remove('sb--loading');
          // Never leave skeletons under the error toast.
          // DOM-truth, not the local flag: a fast-failing reset that
          // INHERITED a prior reset's skeletons must also clear them, or
          // resetBusy() latches true and disables all loading.
          if (self.gridEl.querySelector('.sb-card--skel')) {
            self.renderGrid();
            self.renderStatus();
          }
          self.toast('Could not update results: ' + err.message, true);
        });
      }

      // Current effective column count, from the density var applyCols
      // maintains on the .sb root (fallback is defensive only —
      // resetAndFetch is unreachable pre-activate()).
      effectiveColsNow() {
        const root = this.querySelector('.sb');
        const n = root ? parseInt(root.style.getPropertyValue('--sb-cols'), 10) : NaN;
        return isFinite(n) && n > 0 ? n : 4;
      }

      scrollToResults() {
        const top = this.getBoundingClientRect().top;
        if (top >= 0) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
          // Same measured header clearance the sticky toolbar uses —
          // a static offset would land results behind the fixed header.
          top: window.scrollY + top - (this._stickyTop || SCROLL_HEADER_OFFSET),
          behavior: reduce ? 'auto' : 'smooth'
        });
      }

      renderStatus() {
        this.moreBtn.hidden = !this.hasNextPage;
        if (!this.cards.length) {
          // Chip/clear-all clicks inside the panel are handled by the
          // delegated onClick bound on the element in bindStateEvents.
          this.statusEl.innerHTML = emptyStateHtml(this.state, this.filterDefs);
        } else {
          this.statusEl.textContent = this.cards.length + ' shown';
        }
        // In-place Done relabel — never renderSheetContent here (sheet
        // rebuild resets scroll mid-selection, a known flaw). Runs in both
        // branches: the empty state must still say "Show 0 results".
        if (this.sheetEl) {
          const done = this.sheetEl.querySelector('[data-sb-done]');
          if (done) done.textContent = doneLabel(this.cards.length, this.hasNextPage);
        }
      }

      renderCardInner(card, selFinish, selId) {
        const money = function (n) { return formatMoney(n, card.currency); };
        let html =
          '<a class="sb-card__media" href="' + escapeHtml(card.url) + '" tabindex="-1">' +
          (card.image
            ? '<img loading="lazy" src="' + escapeHtml(card.image) + '"' +
              (card.imageSrcset
                ? ' srcset="' + escapeHtml(card.imageSrcset) + '" sizes="' + CARD_IMG_SIZES + '"'
                : '') +
              ' alt="' + escapeHtml(card.imageAlt) + '">'
            : '') +
          '</a>' +
          '<a class="sb-card__title" href="' + escapeHtml(card.url) + '">' + escapeHtml(card.title) + '</a>';

        if (!card.hasStock) {
          html += '<div class="sb-card__price sb-card__price--oos">Out of stock</div>';
          return html;
        }

        if (card.singleVariant) {
          const v = card.variants[0];
          html += '<div class="sb-card__price">' + money(v.price) + '</div>' +
            '<button type="button" class="sb-card__add" data-sb-add="' + v.id + '">Add to cart</button>';
          return html;
        }

        if (card.finishes.length > 1) {
          html += '<div class="sb-card__finishes">' + card.finishes.map(function (f) {
            const label = f === '' ? 'Normal' : f;
            const active = f === selFinish ? ' is-active' : '';
            return '<button type="button" class="sb-card__finish' + active + '" data-sb-finish="' +
              escapeHtml(f) + '">' + escapeHtml(label) + '</button>';
          }).join('') + '</div>';
        }

        const min = this.state.priceMin;
        const max = this.state.priceMax;
        const pills = card.variants.filter(function (v) { return v.finish === selFinish; });
        let selected = null;
        html += '<div class="sb-card__pills">' + pills.map(function (v) {
          const inRange = (min == null || v.price >= min) && (max == null || v.price <= max);
          const usable = v.available && inRange;
          const active = String(v.id) === String(selId) && usable;
          if (active) selected = v;
          return '<button type="button" class="sb-card__pill' + (active ? ' is-active' : '') + '"' +
            (usable ? '' : ' disabled') + ' data-sb-pill="' + v.id + '">' +
            escapeHtml(v.label || v.title) + ' ' + money(v.price) + '</button>';
        }).join('') + '</div>';

        if (selected) {
          html += '<button type="button" class="sb-card__add" data-sb-add="' + selected.id + '">Add to cart — ' + money(selected.price) + '</button>';
        } else {
          // Honour the active price range; fall back to the true cheapest
          // when no in-stock variant is in range (spec item 1).
          const rangeFrom = fromPriceInRange(card.variants, min, max);
          html += '<div class="sb-card__price">from ' + money(rangeFrom != null ? rangeFrom : card.fromPrice) + '</div>';
        }
        return html;
      }

      cardLiHtml(card) {
        const oos = card.hasStock ? '' : ' sb-card--oos';
        // initial finish = first finish present, so foil-only products
        // (all variants "X Foil") still render their pills
        const finish = card.finishes[0] || '';
        return '<li class="sb-card' + oos + '" data-sb-card="' + escapeHtml(card.handle) +
          '" data-sb-sel-finish="' + escapeHtml(finish) + '">' +
          this.renderCardInner(card, finish, null) + '</li>';
      }

      renderGrid() {
        this._resetSkelsShown = false; // every full rebuild clears reset skels
        const self = this;
        this.gridEl.innerHTML = this.cards.map(function (card) { return self.cardLiHtml(card); }).join('');
      }

      toast(msg, isError) {
        if (window.Toast) {
          isError ? window.Toast.error(msg) : window.Toast.show(msg);
        } else {
          (isError ? console.error : console.log)('[singles-browser] ' + msg);
        }
      }

      addToCart(variantId, button) {
        const self = this;
        const original = button.textContent;
        button.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        }).then(function (res) {
          if (!res.ok) {
            // soldOut derives from the status and survives an
            // unparseable error body.
            const soldOut = res.status === 422;
            return res.json().then(function (j) {
              const e = new Error(j.description || j.message || 'could not add to cart');
              e.soldOut = soldOut;
              throw e;
            }, function () {
              const e = new Error('could not add to cart');
              e.soldOut = soldOut;
              throw e;
            });
          }
          // Success is committed on res.ok — feedback fires NOW; the
          // cart refresh below is best-effort and must never turn a
          // successful add into an error toast.
          button.textContent = 'Added ✓';
          setTimeout(function () {
            if (!button.isConnected) return;
            button.textContent = original;
            button.disabled = false;
          }, 1500);
          self.toast('Added to cart');
          try {
            if (window.Shopify && typeof window.Shopify.adjustCartDropDown === 'function') {
              // Promise.resolve also swallows an async rejection from the
              // renderer — the try/catch alone only covers sync throws.
              Promise.resolve(window.Shopify.adjustCartDropDown()).catch(function () {});
            } else {
              fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
                document.querySelectorAll('.cart-qty').forEach(function (el) {
                  el.textContent = cart.item_count;
                });
              }).catch(function () {});
            }
          } catch (e) {}
        }).catch(function (err) {
          button.disabled = false;
          if (err && err.soldOut) self.markSoldOut(variantId);
          self.toast(err.message, true);
        });
      }

      // 422 race: flip the variant off in card data and re-render just
      // that card. selId null drops the (sold-out) selection — accepted
      // UX; server truth returns on the next refetch.
      markSoldOut(variantId) {
        const card = markVariantUnavailable(this.cards, variantId);
        if (!card) return;
        const li = this.gridEl.querySelector('[data-sb-card="' + escapeHtml(card.handle) + '"]');
        if (!li) return; // mid-refetch — grid no longer shows this card
        li.classList.toggle('sb-card--oos', !card.hasStock);
        li.innerHTML = this.renderCardInner(card, li.getAttribute('data-sb-sel-finish'), null);
      }
    }

    window.customElements.define('singles-browser', SinglesBrowserElement);
  }

  const api = {
    API_VERSION: API_VERSION,
    PAGE_SIZE: PAGE_SIZE,
    escapeQueryText: escapeQueryText,
    quoteTag: quoteTag,
    handleize: handleize,
    normalizeFilterDefs: normalizeFilterDefs,
    extractSetScope: extractSetScope,
    narrowValues: narrowValues,
    buildQuery: buildQuery,
    SORT_OPTIONS: SORT_OPTIONS,
    DEFAULT_SORT: DEFAULT_SORT,
    parsePrice: parsePrice,
    parseState: parseState,
    serializeState: serializeState,
    hasActiveFilters: hasActiveFilters,
    chipButtonsHtml: chipButtonsHtml,
    emptyStateHtml: emptyStateHtml,
    adaptProducts: adaptProducts,
    parseVariantTitle: parseVariantTitle,
    numericId: numericId,
    formatMoney: formatMoney,
    escapeHtml: escapeHtml,
    PRODUCTS_QUERY: PRODUCTS_QUERY,
    validateConfig: validateConfig,
    buildRequestBody: buildRequestBody,
    COLS_OPTIONS: COLS_OPTIONS,
    COLS_DEFAULTS: COLS_DEFAULTS,
    COLS_STORAGE_KEYS: COLS_STORAGE_KEYS,
    COLS_MIN_CARD_WIDTH: COLS_MIN_CARD_WIDTH,
    COLS_GAP: COLS_GAP,
    maxColsForWidth: maxColsForWidth,
    effectiveCols: effectiveCols,
    parseColsPref: parseColsPref,
    activeFilterCount: activeFilterCount,
    doneLabel: doneLabel,
    skeletonCardsHtml: skeletonCardsHtml,
    AUTO_LOAD_PAGES: AUTO_LOAD_PAGES,
    SENTINEL_LOOKAHEAD: SENTINEL_LOOKAHEAD,
    shouldAutoLoad: shouldAutoLoad,
    nextCursor: nextCursor,
    fromPriceInRange: fromPriceInRange,
    markVariantUnavailable: markVariantUnavailable,
    CARD_IMG_SIZES: CARD_IMG_SIZES
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.SinglesBrowser = api;
})();
